/**
 * CNC export — one design, a program per ring size (`exportBandCncBatch`,
 * contract v66): reading the sizes a person types, filling a range, and
 * packing the results into one .zip with a manifest.
 *
 * Pure: no React, no DOM, no viewer. `scripts/check-cnc-ui.ts` runs it
 * headless.
 *
 * The viewer is still the authority: it reads every size again, names every
 * file, and gates every program. What lives here is what the host needs to
 * refuse a typo before a batch runs (sizes the viewer could not read would come
 * back as error items, several seconds later) and to pack what came back.
 */
import { UK_RING_SIZES } from "../../../shared/api-contract";
import type { CncBatchItem, CncBatchResult, CncSizeSystem } from "../../../shared/api-contract";
import { asciiText, cncCompanionNames, formatCncSize, splitCheckId } from "./cncFormat";
import type { ZipEntry } from "./zip";

/** Most sizes in one batch (the viewer's `CNC_BATCH_MAX_SIZES`): each runs the full gate, twice. */
export const CNC_BATCH_MAX_SIZES = 40;

/**
 * The numeric sizes the viewer can turn into a bore (`band/cnc/stock.ts`
 * `ringSizeToBoreDiameter`). UK is its letter table, A … Z½. A US size outside
 * the band builder's own 3 … 13 is still READ, and comes back withheld with a
 * `blank.size` fail — the viewer says why, so the host does not second-guess it.
 */
export const CNC_NUMERIC_SIZE_RANGE: Record<Exclude<CncSizeSystem, "UK">, { min: number; max: number }> = {
  US: { min: 0, max: 16 },
  EU: { min: 35, max: 80 },
  IN: { min: 6, max: 30 },
};

/** The quick-fill steps each system offers, in its own words. */
export const CNC_RANGE_STEPS: Record<CncSizeSystem, readonly { id: number; label: string }[]> = {
  US: [{ id: 0.25, label: "Quarter" }, { id: 0.5, label: "Half" }, { id: 1, label: "Whole" }],
  EU: [{ id: 0.5, label: "Half" }, { id: 1, label: "Whole" }],
  UK: [{ id: 0.5, label: "Half letters" }, { id: 1, label: "Whole letters" }],
  IN: [{ id: 1, label: "Whole" }],
};

const SYSTEM_NAME: Record<CncSizeSystem, string> = { US: "US", EU: "EU", UK: "UK", IN: "Indian" };
const A_SIZE: Record<CncSizeSystem, string> = { US: "a US size", EU: "an EU size", UK: "a UK size", IN: "an Indian size" };

/** One size as the viewer is sent it, and as the files will name it. */
export interface CncBatchSizeEntry {
  /** What `exportBandCncBatch` is sent: a number, or a UK letter string. */
  value: number | string;
  /** As the header and the file name write it (`10.75`, `K.5`). */
  label: string;
}

const FRACTIONS: Record<string, number> = { "¼": 0.25, "½": 0.5, "¾": 0.75 };

/** One typed size, read in `system` — or why it cannot be. */
export function readCncSize(token: string, system: CncSizeSystem): CncBatchSizeEntry | { error: string } {
  const raw = token.trim();
  if (system === "UK") {
    const m = /^([A-Za-z])(½|\.5|1\/2)?$/.exec(raw);
    const letter = m ? `${m[1].toUpperCase()}${m[2] ? "½" : ""}` : null;
    if (!letter || !UK_RING_SIZES.some((r) => r.label === letter)) {
      return { error: `"${raw}" is not a UK size — a letter A to Z, a half written K½ or K.5.` };
    }
    const label = letter.replace("½", ".5");
    return { value: label, label };
  }
  const m = /^(\d+(?:\.\d+)?)([¼½¾])?$/.exec(raw);
  if (!m || (m[2] && m[1].includes("."))) return { error: `"${raw}" is not ${A_SIZE[system]}.` };
  const n = Number(m[1]) + (m[2] ? FRACTIONS[m[2]] : 0);
  const { min, max } = CNC_NUMERIC_SIZE_RANGE[system];
  if (n < min || n > max) return { error: `${SYSTEM_NAME[system]} ${formatCncSize(n)} is outside the ${SYSTEM_NAME[system]} sizes the program can be made for (${min} to ${max}).` };
  const label = formatCncSize(n);
  return { value: Number(label), label };
}

export interface ParsedCncSizes {
  sizes: CncBatchSizeEntry[];
  /** Every token that could not be read, and the count problem if there is one. */
  errors: string[];
  /** Repeats that were dropped (the second "10.75", or "K½" after "K.5"). */
  repeats: string[];
}

/**
 * The sizes in a typed list — commas, semicolons, spaces or new lines between
 * them — in order, each once. More than `CNC_BATCH_MAX_SIZES` is an error, not
 * a silent cut.
 */
export function parseCncSizes(text: string, system: CncSizeSystem): ParsedCncSizes {
  const sizes: CncBatchSizeEntry[] = [];
  const errors: string[] = [];
  const repeats: string[] = [];
  for (const token of text.split(/[\s,;]+/).filter(Boolean)) {
    const read = readCncSize(token, system);
    if ("error" in read) errors.push(read.error);
    else if (sizes.some((s) => s.label === read.label)) repeats.push(read.label);
    else sizes.push(read);
  }
  if (sizes.length > CNC_BATCH_MAX_SIZES) {
    errors.push(`${sizes.length} sizes — at most ${CNC_BATCH_MAX_SIZES} in one go.`);
  }
  return { sizes, errors, repeats };
}

/**
 * Every size from `from` to `to` (both included) in steps of `step`, as the
 * text the size box takes. UK steps through its letter table (½ = a half
 * letter). An error when the ends cannot be read, run backwards, or would make
 * more than `CNC_BATCH_MAX_SIZES` sizes.
 */
export function cncSizeRange(system: CncSizeSystem, from: string, to: string, step: number): { labels: string[] } | { error: string } {
  const a = readCncSize(from, system);
  if ("error" in a) return { error: `From: ${a.error}` };
  const b = readCncSize(to, system);
  if ("error" in b) return { error: `To: ${b.error}` };
  if (!(step > 0)) return { error: "Pick a step." };

  let labels: string[];
  if (system === "UK") {
    const index = (label: string) => UK_RING_SIZES.findIndex((r) => r.label === label.replace(".5", "½"));
    const i0 = index(a.label);
    const i1 = index(b.label);
    if (i1 < i0) return { error: `${b.label} comes before ${a.label} — put the smaller size first.` };
    const stride = Math.max(1, Math.round(step * 2));
    if (Math.floor((i1 - i0) / stride) + 1 > CNC_BATCH_MAX_SIZES) return { error: tooMany(Math.floor((i1 - i0) / stride) + 1) };
    labels = [];
    for (let i = i0; i <= i1; i += stride) labels.push(UK_RING_SIZES[i].label.replace("½", ".5"));
  } else {
    const lo = Number(a.label);
    const hi = Number(b.label);
    if (hi < lo) return { error: `${b.label} is smaller than ${a.label} — put the smaller size first.` };
    // Count in whole steps so 0.25 × 12 lands on the end exactly.
    const count = Math.floor((hi - lo) / step + 1e-9) + 1;
    if (count > CNC_BATCH_MAX_SIZES) return { error: tooMany(count) };
    labels = Array.from({ length: count }, (_, i) => formatCncSize(lo + i * step));
  }
  return { labels };
}

const tooMany = (n: number) => `That is ${n} sizes — at most ${CNC_BATCH_MAX_SIZES} in one go. Use a bigger step or a shorter range.`;

// ── Reading a batch result ──────────────────────────────────────────────────

export type CncBatchVerdict =
  | { kind: "ok"; text: string }
  | { kind: "withheld"; text: string; firstFail: { id: string; message: string } | null }
  | { kind: "error"; text: string; error: string };

/** One size's outcome in words: a program, withheld (with the first fail), or not made at all. */
export function batchVerdict(item: CncBatchItem): CncBatchVerdict {
  if (item.error !== undefined) return { kind: "error", text: "Not made", error: item.error };
  if (item.ok && item.nc) return { kind: "ok", text: "Program ready" };
  return { kind: "withheld", text: "Withheld", firstFail: item.fails[0] ?? null };
}

/** What a batch .zip is about: written into the manifest and its name. */
export interface CncBatchZipContext {
  model: string;
  label: string;
  system: CncSizeSystem;
  profileName: string;
  calibrated: boolean;
  calibratedBy?: string;
  calibratedAt?: string;
  /** When the batch was made (the manifest's time, and every entry's date). */
  madeAt: Date;
}

/** `368222-UGTH2310-US-12-sizes.zip`. */
export function batchZipName(ctx: Pick<CncBatchZipContext, "model" | "label" | "system">, count: number): string {
  return `${ctx.model}-${ctx.label}-${ctx.system}-${count}-size${count === 1 ? "" : "s"}.zip`;
}

/** `368222-UGTH2310-US-MANIFEST.txt`. */
export function batchManifestName(ctx: Pick<CncBatchZipContext, "model" | "label" | "system">): string {
  return `${ctx.model}-${ctx.label}-${ctx.system}-MANIFEST.txt`;
}

/** The files one batch item puts in the .zip, in order: program, air cut, setup sheet, .3dm. */
function itemFiles(item: CncBatchItem): ZipEntry[] {
  const names = cncCompanionNames(item.fileName);
  const files: ZipEntry[] = [];
  // Only a size whose gate passed carries program text; nothing here may add one.
  if (item.ok && item.nc) files.push({ name: item.fileName, data: item.nc });
  if (item.ok && item.airCut && names) files.push({ name: names.airCut, data: item.airCut });
  if (names) files.push({ name: names.setup, data: item.setupSheet });
  if (item.cam3dm) files.push({ name: item.cam3dmFileName ?? names?.cam3dm ?? `${item.fileName}.cam.3dm`, data: item.cam3dm });
  return files;
}

/**
 * The manifest: one line per size with its verdict and its files, the first
 * fail of a withheld size, the error of one that was not made. ASCII with
 * CRLF line ends, like every other file the shop gets.
 */
export function batchManifest(result: CncBatchResult, ctx: CncBatchZipContext, left: readonly { size: string; why: string }[] = []): string {
  const items = result.items;
  const counts = { ok: 0, withheld: 0, error: 0 };
  for (const it of items) counts[batchVerdict(it).kind]++;

  const lines: string[] = [
    `CNC programs by size - model ${ctx.model}, label ${ctx.label}`,
    `Size system: ${SYSTEM_NAME[ctx.system]}`,
    `Machine profile: ${ctx.profileName}${ctx.calibrated
      ? ` (proven${ctx.calibratedBy ? ` by ${ctx.calibratedBy}` : ""}${ctx.calibratedAt ? ` on ${ctx.calibratedAt}` : ""})`
      : " - NOT CALIBRATED"}`,
    `Made: ${ctx.madeAt.toISOString()}`,
    `Sizes: ${items.length} asked - ${counts.ok} with a program, ${counts.withheld} withheld, ${counts.error} not made.`,
    "",
  ];
  if (!ctx.calibrated) {
    lines.push(
      "UNCALIBRATED: this machine profile has not been proven at the shop. Run the air-cut file",
      "first, then a direction test and a brass test piece, before cutting metal.",
      "",
    );
  }
  lines.push("A withheld size has no program and no air cut: its setup sheet says why.", "");

  for (const it of items) {
    const v = batchVerdict(it);
    const head = v.kind === "ok" ? "PROGRAM" : v.kind === "withheld" ? "WITHHELD" : "NOT MADE";
    lines.push(`${SYSTEM_NAME[ctx.system]} ${it.size}: ${head}`);
    const skipped = left.find((l) => l.size === it.size);
    if (skipped) {
      lines.push(`  left out of this archive: ${skipped.why}`);
    } else {
      const files = itemFiles(it).map((f) => f.name);
      if (files.length) lines.push(`  files: ${files.join(", ")}`);
    }
    if (v.kind === "withheld") {
      if (v.firstFail) lines.push(`  first fail: ${v.firstFail.message} [${v.firstFail.id}]`);
      if (it.fails.length > 1) lines.push(`  and ${it.fails.length - 1} more fail${it.fails.length === 2 ? "" : "s"} - see the setup sheet`);
    }
    if (v.kind === "error") lines.push(`  why: ${v.error}`);
    if (it.warns.length) lines.push(`  ${it.warns.length} thing${it.warns.length === 1 ? "" : "s"} to read before running - see the setup sheet`);
    // Not gate checks (a .3dm that could not be written, or was withheld), so not on the setup sheet either.
    for (const note of it.notes ?? []) {
      const { id, message } = splitCheckId(note);
      lines.push(`  note: ${message}${id ? ` [${id}]` : ""}`);
    }
  }
  return lines.map((l) => asciiText(l)).join("\r\n") + "\r\n";
}

/**
 * Everything one batch puts in its .zip: the manifest first, then each size's
 * files in the order the sizes were asked for. A size whose file name another
 * size already took (two spellings of one size) is left out and the manifest
 * says so — two different programs must never share a name.
 */
export function batchZipEntries(result: CncBatchResult, ctx: CncBatchZipContext): { entries: ZipEntry[]; left: { size: string; why: string }[] } {
  const taken = new Set<string>();
  const left: { size: string; why: string }[] = [];
  const files: ZipEntry[] = [];
  for (const it of result.items) {
    const mine = itemFiles(it);
    const clash = mine.find((f) => taken.has(f.name));
    if (clash) {
      left.push({ size: it.size, why: `${clash.name} is already in the archive from an earlier size` });
      continue;
    }
    for (const f of mine) {
      taken.add(f.name);
      files.push(f);
    }
  }
  return { entries: [{ name: batchManifestName(ctx), data: batchManifest(result, ctx, left) }, ...files], left };
}
